import { useEffect, useState } from "react";
import { getIsStandalonePWA } from "../helpers/getIsStandalonePWA";

/**
 * Hook to manage PWA installation in browsers that support the
 * `beforeinstallprompt` event (Chrome/Edge, mainly on Android).
 *
 * Responsibilities:
 * - Listen for the `beforeinstallprompt` event and store the deferred prompt.
 * - Expose `canInstall` when an install prompt is available.
 * - Detect when the app is already installed (`installed`).
 * - Provide the `promptInstall` function to trigger the native install dialog.
 */
type BeforeInstallPromptEvent = Event & {
	prompt: () => Promise<void>;
	userChoice: Promise<{
		outcome: "accepted" | "dismissed";
		platform: string;
	}>;
};

export const usePWAInstall = () => {
	// Stored `beforeinstallprompt` event so we can call `prompt()` later.
	const [deferredPrompt, setDeferredPrompt] =
		useState<BeforeInstallPromptEvent | null>(null);
	// Indicates whether the browser currently allows showing the install dialog.
	const [canInstall, setCanInstall] = useState(false);
	// Indicates whether the app is already installed / running in standalone mode.
	const [installed, setInstalled] = useState(false);

	useEffect(() => {
		// Do nothing in SSR or environments without `window`.
		if (typeof window === "undefined") {
			return;
		}

		// Fired when the browser detects that the PWA is installable.
		const beforeInstallHandler = (e: Event) => {
			// Prevent the browser from showing its own automatic prompt.
			e.preventDefault();
			// Store the event so we can trigger it on demand.
			// console.log("[PWA] beforeinstallprompt fired", {
			// 	href: window.location.href,
			// 	userAgent: window.navigator.userAgent
			// });
			setDeferredPrompt(e as BeforeInstallPromptEvent);
			setCanInstall(true);
		};

		// Fired when the user completes the PWA installation.
		const appInstalledHandler = () => {
			// console.log("[PWA] appinstalled event fired");
			setInstalled(true);
			setDeferredPrompt(null);
			setCanInstall(false);
		};

		// Checks whether the app is already running in standalone (installed) mode.
		const checkDisplayMode = () => {
			const isStandalone = getIsStandalonePWA();
			// console.log("[PWA] display mode check", { isStandalone, href: window.location.href });
			setInstalled(isStandalone);
		};

		window.addEventListener(
			"beforeinstallprompt",
			beforeInstallHandler as EventListener
		);
		window.addEventListener("appinstalled", appInstalledHandler);

		// On mount, check if we are already in standalone mode.
		checkDisplayMode();

		// Clean up listeners when the hook unmounts.
		return () => {
			window.removeEventListener(
				"beforeinstallprompt",
				beforeInstallHandler as EventListener
			);
			window.removeEventListener("appinstalled", appInstalledHandler);
		};
	}, []);

	/**
	 * Triggers the native install dialog if there is a stored `beforeinstallprompt`.
	 * Returns:
	 * - "accepted" if the user accepts installation,
	 * - "dismissed" if the user cancels it,
	 * - `null` if no prompt was available.
	 */
	const promptInstall = async () => {
		if (!deferredPrompt) {
			// eslint-disable-next-line no-console
			// console.log("[PWA] promptInstall called but no deferredPrompt available");
			return null;
		}

		await deferredPrompt.prompt();
		const choiceResult = await deferredPrompt.userChoice;
		// eslint-disable-next-line no-console
		// console.log("[PWA] promptInstall userChoice", choiceResult);

		if (choiceResult.outcome === "accepted") {
			setCanInstall(false);
		}

		setDeferredPrompt(null);
		return choiceResult.outcome;
	};

	return { canInstall, installed, promptInstall };
};
